import React from "react";
import { useEvent } from "../hooks/useEvent";

type GetBoundingClientRect = (element: Element) => DOMRect;

const Context = React.createContext<GetBoundingClientRect | null>(null);

const nullRect = (): DOMRect => {
  if ("DOMRect" in window) {
    return new DOMRect();
  }

  /**
   * JSDom does not implement DOMRect, so we have to mock
   */
  return {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    toJSON: () => "",
  };
};

export const useGetBoundingClientRect = () => {
  const context = React.useContext(Context);

  return useEvent((element: Element | null) => {
    if (!element) {
      return nullRect();
    }

    if (context === null) {
      return element?.getBoundingClientRect() ?? nullRect();
    }

    return context(element);
  });
};

export const useGetBoundingClientRectRef = () => {
  const getBoundingClientRect = useGetBoundingClientRect();

  return useEvent((ref: React.RefObject<Element>): DOMRect => {
    return getBoundingClientRect(ref.current);
  });
};

export const MockGetBoundingClientRectProvider: React.FC<{ callback: GetBoundingClientRect }> = (props) => {
  const { children, callback } = props;

  return <Context.Provider value={callback}>{children}</Context.Provider>;
};
